import { memo, useCallback, useEffect, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import { isBedbankOffer, isLEHotel } from 'lib/offer/offerTypes'
import Heading from 'LuxKit/Typography/Heading'
import TextButton from 'LuxKit/Button/TextButton'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import Group from 'components/utils/Group'
import noop from 'lib/function/noop'
import { capitalise } from 'lib/string/stringUtils'
import { normaliseOffersForMap } from 'tripPlanner/utils/mapMarkerTransforms'
import type { HotelMapViewData, TripItineraryData } from 'tripPlanner/api/types'
import { isOfferBookedInItinerary } from 'tripPlanner/utils/chatItinerary'
import ChatHotelCarouselCard from './ChatHotelCarouselCard'
import OfferCardLoadingSkeleton from 'components/OfferList/OfferCards/OfferCardLoadingSkeleton'
import LoaderPlayStateContext from 'contexts/LoaderPlayStateContext'
import AssistChip from 'LuxKit/Chips/AssistChip'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import OccupancyPicker from 'components/Common/OccupancyPicker/OccupancyPicker'
import FormattedOccupancy from 'components/Common/OccupancyPicker/FormattedOccupancy'
import BodyText from 'LuxKit/Typography/BodyText'
import SearchDatesPicker from 'components/SearchV2/Components/Mobile/DateSearchFloatingPanel/SearchDatePicker'
import FlexibleDatePicker from 'components/Search/SearchForm/SearchDateInput/FlexibleDatePicker'
import SearchDateToggle from 'components/Search/SearchForm/SearchDateInput/SearchDateToggle'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import type { OfferClickSearchParams } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatMessageActionsContext'
import type { SendMessageOptions } from 'tripPlanner/components/AddToTrip/useAddHotelToTrip'
import LineTimesIcon from 'LuxKit/Icons/line/LineTimesIcon'
import useCarouselOccupancyFilter, { isDefaultOccupancy } from './useCarouselOccupancyFilter'
import useCarouselDateFilter from './useCarouselDateFilter'
import useCarouselOfferSearch from './useCarouselOfferSearch'
import { useCarouselPaginationTracker } from 'tripPlanner/hooks/useCarouselPaginationTracker'
import { useChatStreamStatus } from 'tripPlanner/pages/chat/ChatTripPlannerPage/contexts/ChatStreamStatusContext'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useTripPlannerOfferListTracking from 'tripPlanner/pages/chat/ChatTripPlannerPage/hooks/useTripPlannerOfferListTracking'

const TARGET_LOADING_CARDS = 10

interface Props {
  offerIds: Array<string>
  /** The NLP query - used as title and for map filter label */
  searchQuery?: string
  /** Switches between hotel (check-in modal) and experience (stop/day dropdown) add-to-trip behaviour */
  cardType?: 'hotel' | 'experience'
  onHotelOffersDisplayed?: (hotelInfo: string) => void
  onOfferClick?: (data: { offerId: string; searchParams?: OfferClickSearchParams; offerType?: string }) => void
  onOfferHover?: (offerId: string | undefined) => void
  /** Called when "Map view" button is clicked with normalized offers */
  onMapViewClick?: (data: HotelMapViewData) => void
  /** Current trip itinerary data for "Add to trip" on each card */
  itineraryData?: TripItineraryData
  /** Sends a chat message (used by "Add to trip" on each card) */
  onSendMessage?: (message: string, options?: SendMessageOptions) => void
  trackingKey: string
}

const Container = styled.div`
  margin: ${rem(16)} 0;
`

const CardWrapper = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border-radius: ${rem(16)};
  padding: ${rem(20)};
  box-shadow: ${props => props.theme.shadow.flat.small};
`

const CarouselWrapper = styled.div`
  margin-top: ${rem(16)};
  position: relative;

  [data-testid*="bookmark-button"] {
    display: none !important;
  }

  button[data-direction] {
    z-index: 99;
  }
`

const DropdownWidthSpacer = styled.div`
  width: ${rem(700)};
`

const EmptyStateWrapper = styled.div`
  padding: ${rem(40)} 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(8)};
`

function formatHotelOffersInfo(offers: Array<App.AnyOffer>): string {
  return offers.map((offer, index) => {
    const lines: Array<string> = []
    lines.push(`${index + 1}. ${offer.name || offer.id}`)

    if (isLEHotel(offer)) {
      if ('description' in offer && offer.description) {
        lines.push(`   Description: ${offer.description}`)
      }
      if ('location' in offer && offer.location) {
        lines.push(`   Location: ${offer.location}`)
      }
      if (offer.property?.name) {
        lines.push(`   Property: ${offer.property.name}`)
      }
      if ('highlights' in offer && offer.highlights) {
        lines.push(`   Highlights: ${offer.highlights}`)
      }
    } else if (isBedbankOffer(offer)) {
      if ('description' in offer && offer.description) {
        lines.push(`   Description: ${offer.description}`)
      }
      if ('tagline' in offer && offer.tagline) {
        lines.push(`   Tagline: ${offer.tagline}`)
      }
      if (offer.property?.location) {
        lines.push(`   Location: ${offer.property.location}`)
      }
      if (offer.property?.name) {
        lines.push(`   Property: ${offer.property.name}`)
      }
      if (offer.property?.highlights) {
        lines.push(`   Highlights: ${offer.property.highlights}`)
      }
    }

    lines.push(`   Offer Type: ${offer.type}`)
    lines.push(`   Offer ID: ${offer.id}`)
    return lines.join('\n')
  }).join('\n\n')
}

function ChatHotelCarousel({
  offerIds,
  searchQuery,
  cardType = 'hotel',
  onHotelOffersDisplayed = noop,
  onOfferClick = noop,
  onOfferHover = noop,
  onMapViewClick = noop,
  itineraryData,
  onSendMessage,
  trackingKey,
}: Props) {
  const intl = useIntl()
  const { chatId } = useChatStreamStatus()

  const defaultHotelTitleLabel = intl.formatMessage({
    id: 'l06aaJjBUwR-eQ-5874E',
    defaultMessage: 'Top Hotel Picks',
    description: 'Default heading for the hotel carousel when no search query is provided',
  })
  const defaultExperienceTitleLabel = intl.formatMessage({
    id: 'vJhmJAn2elKiRffXAdo5',
    defaultMessage: 'Top Experience Picks',
    description: 'Default heading for the experience carousel when no search query is provided',
  })
  const defaultTitleLabel = cardType === 'experience' ? defaultExperienceTitleLabel : defaultHotelTitleLabel
  const mapViewLabel = intl.formatMessage({
    id: 'FAktCqmZIbljAt7U9Qji',
    defaultMessage: 'Map view',
    description: 'Button label to switch to map view of hotel results',
  })
  const applyLabel = intl.formatMessage({
    id: 'WwpiGSJ1HEBTcT0uO6U9',
    defaultMessage: 'Apply',
    description: 'Button label to apply filter selections in hotel carousel dropdowns',
  })
  const clearFiltersLabel = intl.formatMessage({
    id: 'OXghqAlhRXvpXTLguQRu',
    defaultMessage: 'Clear filters',
    description: 'Button label to reset all active hotel carousel filters',
  })
  const anytimeLabel = intl.formatMessage({
    id: 'pXZTaVZTYZmkf9DrwAg_',
    defaultMessage: 'Anytime',
    description: 'Date chip label when no specific dates are selected for hotel search',
  })
  const noHotelsMessage = intl.formatMessage({
    id: 'mnXjS_ufg-DZDPW5_gWl',
    defaultMessage: 'No hotels available for your selected filters.',
    description: 'Empty state message when hotel carousel filters return no results',
  })

  const occupancy = useCarouselOccupancyFilter()
  const dateFilter = useCarouselDateFilter({ anytimeLabel })
  const { clear: clearOccupancy } = occupancy
  const { clear: clearDates } = dateFilter

  const filterActive = !isDefaultOccupancy(occupancy.rooms) ||
    dateFilter.dateMode !== DATE_SEARCH_OPTION_IDS.ANYTIME

  const canFilter = !!searchQuery && cardType !== 'experience'

  const search = useCarouselOfferSearch({
    searchQuery,
    rooms: occupancy.rooms,
    filterActive,
    dateMode: dateFilter.dateMode,
    checkIn: dateFilter.checkIn,
    checkOut: dateFilter.checkOut,
    flexibleMonths: dateFilter.flexibleMonths,
    flexibleNights: dateFilter.flexibleNights,
    originalOfferIds: offerIds,
  })

  const selectedDatesAndRoomsFilter = useMemo<App.OfferListFilters | undefined>(() => {
    if (dateFilter.dateMode !== DATE_SEARCH_OPTION_IDS.SPECIFIC || !dateFilter.checkIn || !dateFilter.checkOut) return undefined
    return {
      checkIn: dateFilter.checkIn.format(ISO_DATE_FORMAT),
      checkOut: dateFilter.checkOut.format(ISO_DATE_FORMAT),
      rooms: occupancy.rooms,
    }
  }, [dateFilter.checkIn, dateFilter.checkOut, dateFilter.dateMode, occupancy.rooms])

  const currentSearchParams = useMemo<OfferClickSearchParams | undefined>(() => {
    if (!filterActive) return undefined
    const params: OfferClickSearchParams = { rooms: occupancy.rooms }
    if (dateFilter.dateMode === DATE_SEARCH_OPTION_IDS.SPECIFIC && dateFilter.checkIn && dateFilter.checkOut) {
      params.checkIn = dateFilter.checkIn.format(ISO_DATE_FORMAT)
      params.checkOut = dateFilter.checkOut.format(ISO_DATE_FORMAT)
    } else if (dateFilter.dateMode === DATE_SEARCH_OPTION_IDS.FLEXIBLE) {
      if (dateFilter.flexibleMonths) params.flexibleMonths = dateFilter.flexibleMonths
      if (dateFilter.flexibleNights) params.flexibleNights = dateFilter.flexibleNights
    }
    return params
  }, [dateFilter.dateMode, dateFilter.checkIn, dateFilter.checkOut, dateFilter.flexibleMonths, dateFilter.flexibleNights, filterActive, occupancy.rooms])

  const handleClearFilters = useCallback(() => {
    clearOccupancy()
    clearDates()
  }, [clearOccupancy, clearDates])

  const hasNotifiedRef = useRef(false)
  useEffect(() => {
    if (!hasNotifiedRef.current && !filterActive && !search.offersFetching && search.offers.length > 0) {
      hasNotifiedRef.current = true
      const hotelInfo = formatHotelOffersInfo(search.offers)
      onHotelOffersDisplayed(hotelInfo)
    }
  }, [search.offers, search.offersFetching, onHotelOffersDisplayed, filterActive])

  const handleMapViewClick = useCallback(() => {
    const normalisedOffers = normaliseOffersForMap(search.offers)
    const query = searchQuery || defaultTitleLabel
    onMapViewClick({ searchQuery: query, offers: normalisedOffers })
  }, [onMapViewClick, search.offers, searchQuery, defaultTitleLabel])

  const displayTitle = capitalise(searchQuery || defaultTitleLabel)
  const tracking = useTripPlannerOfferListTracking(
    cardType === 'experience' ? 'experienceChatCarousel' : 'hotelChatCarousel',
    trackingKey,
  )

  const hasMapViewButton = onMapViewClick !== noop

  const offerNameMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const offer of search.offers) {
      if (offer.name) {
        map.set(offer.id, offer.name)
      }
    }
    return map
  }, [search.offers])

  const offerTypeMap = useMemo(() => {
    const map = new Map<string, string>()
    for (const offer of search.offers) {
      map.set(offer.id, offer.type)
    }
    return map
  }, [search.offers])

  const offerById = useMemo(() => {
    return new Map(search.offers.map((offer) => [offer.id, offer]))
  }, [search.offers])

  const bookedOfferIds = useMemo(() => {
    const set = new Set<string>()
    for (const id of search.filteredOfferIds) {
      if (isOfferBookedInItinerary(id, itineraryData)) {
        set.add(id)
      }
    }
    return set
  }, [search.filteredOfferIds, itineraryData])

  const offerGeoMap = useMemo(() => {
    const map = new Map<string, { city?: string; coordinates?: { lat: number; lng: number } }>()
    for (const offer of search.offers) {
      let city: string | undefined
      let coordinates: { lat: number; lng: number } | undefined

      if (isLEHotel(offer)) {
        city = typeof offer.location === 'string' ? offer.location : undefined
      } else if (isBedbankOffer(offer)) {
        city = offer.property?.location
      }

      const property = (isLEHotel(offer) || isBedbankOffer(offer)) ? offer.property : undefined
      if (property?.latitude != null && property?.longitude != null) {
        coordinates = { lat: property.latitude, lng: property.longitude }
      } else if (offer.type === 'experience') {
        const { location } = offer
        if (location.latitude != null && location.longitude != null) {
          coordinates = { lat: location.latitude, lng: location.longitude }
        }
        city = location.city
      }

      if (city || coordinates) {
        map.set(offer.id, { city, coordinates })
      }
    }
    return map
  }, [search.offers])

  const handleOfferClickWithParams = useCallback((offerId: string) => {
    onOfferClick({ offerId, searchParams: currentSearchParams, offerType: offerTypeMap.get(offerId) })
  }, [onOfferClick, currentSearchParams, offerTypeMap])

  const { onCarouselScroll } = useCarouselPaginationTracker(chatId)

  const offerClickHandler = onOfferClick !== noop ? handleOfferClickWithParams : undefined
  const offerHoverHandler = onOfferHover !== noop ? onOfferHover : undefined

  if (offerIds.length === 0 && !filterActive) {
    return null
  }

  return (
    <Container>
      <CardWrapper>
        <Group direction="vertical" gap={16}>

          <Group direction="horizontal" verticalAlign="center" horizontalAlign="space-between">
            <Heading variant="heading4" colour="neutral-one">
              {displayTitle}
            </Heading>
            {hasMapViewButton && (
              <TextButton
                variant="dark"
                kind="tertiary"
                size="medium"
                onClick={handleMapViewClick}
                startIcon={<LineMapIcon />}
                disabled={search.offersFetching}
              >
                {mapViewLabel}
              </TextButton>
            )}
          </Group>
          {canFilter && (
            <Group direction="horizontal" gap={8}>
              <AssistChip
                ref={occupancy.triggerRef}
                variant="outline"
                size="medium"
                endIcon={<DropdownChevron open={occupancy.open} />}
                onClick={occupancy.handleToggle}
              >
                <FormattedOccupancy occupancies={occupancy.rooms} />
              </AssistChip>
              <DropdownSheet
                size="M"
                triggerRef={occupancy.triggerRef}
                anchorRef={occupancy.triggerRef}
                open={occupancy.open}
                onClose={occupancy.handleClose}
                primaryActionProps={{
                  children: applyLabel,
                  type: 'button',
                  onClick: occupancy.handleApply,
                }}
                footerStart={
                  <Group direction="horizontal" horizontalAlign="end">
                    <BodyText variant="large">
                      <FormattedOccupancy occupancies={occupancy.pendingRooms} />
                    </BodyText>
                  </Group>
                }
              >
                <OccupancyPicker
                  occupancies={occupancy.pendingRooms}
                  onChange={occupancy.handlePendingChange}
                  enableMultiRoom
                  childVariant="children-only"
                />
              </DropdownSheet>
              <AssistChip
                ref={dateFilter.triggerRef}
                variant="outline"
                size="medium"
                endIcon={<DropdownChevron open={dateFilter.open} />}
                onClick={dateFilter.handleToggle}
              >
                {dateFilter.chipLabel}
              </AssistChip>
              <DropdownSheet
                size="content"
                placement="bottom"
                triggerRef={dateFilter.triggerRef}
                anchorRef={dateFilter.triggerRef}
                open={dateFilter.open}
                onClose={dateFilter.handleClose}
                primaryActionProps={{
                  children: applyLabel,
                  type: 'button',
                  disabled: dateFilter.isApplyDisabled,
                  onClick: dateFilter.handleApply,
                }}
              >
                <DropdownWidthSpacer />
                <Group direction="vertical" gap={24}>
                  <Group direction="horizontal" horizontalAlign="center">
                    <SearchDateToggle
                      onSelectSpecific={dateFilter.handleSelectSpecific}
                      onSelectFlexible={dateFilter.handleSelectFlexible}
                      onSelectAnytime={dateFilter.handleSelectAnytime}
                      anytimeDateSelected={dateFilter.pendingDateMode === DATE_SEARCH_OPTION_IDS.ANYTIME}
                      dateSearchOptionId={dateFilter.pendingDateMode}
                    />
                  </Group>
                  {dateFilter.shouldShowFlexiblePicker && (
                    <FlexibleDatePicker
                      onDurationChange={dateFilter.handlePendingFlexibleNightsChange}
                      onMonthChange={dateFilter.handlePendingFlexibleMonthsChange}
                      previouslySelectedDuration={dateFilter.pendingFlexibleNights}
                      previouslySelectedMonths={dateFilter.pendingFlexibleMonths || undefined}
                    />
                  )}
                  {dateFilter.shouldShowSpecificPicker && (
                    <SearchDatesPicker
                      type="single"
                      onDatesChange={dateFilter.handlePendingDatesChange}
                      startDate={dateFilter.pendingCheckIn}
                      endDate={dateFilter.pendingCheckOut}
                      minDate={dateFilter.today}
                      maxDate={dateFilter.calendarMaxDate}
                    />
                  )}
                </Group>
              </DropdownSheet>
              {filterActive && (
                <AssistChip
                  variant="filled"
                  size="medium"
                  endIcon={<LineTimesIcon />}
                  onClick={handleClearFilters}
                >
                  {clearFiltersLabel}
                </AssistChip>
              )}
            </Group>
          )}
        </Group>
        <CarouselWrapper>
          {search.showLoadingCards && (
            <LoaderPlayStateContext.Provider value="running">
              <CardCarousel snap="start" gutterStyle="overflow" gap={20} pageSize="auto">
                {Array.from({ length: TARGET_LOADING_CARDS }, (_, i) => (
                  <OfferCardLoadingSkeleton key={`skeleton-${i}`} variant="card-default" size="small" />
                ))}
              </CardCarousel>
            </LoaderPlayStateContext.Provider>
          )}
          {!search.showLoadingCards && search.showEmptyState && (
            <EmptyStateWrapper>
              <BodyText variant="large" colour="neutral-three" align="center">
                {noHotelsMessage}
              </BodyText>
              <TextButton variant="dark" kind="tertiary" size="small" onClick={handleClearFilters}>
                {clearFiltersLabel}
              </TextButton>
            </EmptyStateWrapper>
          )}
          {!search.showLoadingCards && search.filteredOfferIds.length > 0 && (
            <OfferListEventsProvider tracking={tracking}>
              <CardCarousel snap="start" gutterStyle="overflow" gap={20} pageSize="auto" onScroll={onCarouselScroll}>
                {search.filteredOfferIds.map((offerId, index) => (
                  <ChatHotelCarouselCard
                    key={offerId}
                    offerId={offerId}
                    index={index}
                    cardType={cardType}
                    offerName={offerNameMap.get(offerId)}
                    offerType={offerTypeMap.get(offerId)}
                    offerCity={offerGeoMap.get(offerId)?.city}
                    offerCoordinates={offerGeoMap.get(offerId)?.coordinates}
                    itineraryData={itineraryData}
                    isBooked={bookedOfferIds.has(offerId)}
                    onSendMessage={onSendMessage}
                    onOfferClick={offerClickHandler}
                    onOfferHover={offerHoverHandler}
                    selectedDatesAndRoomsFilter={selectedDatesAndRoomsFilter}
                    filterNights={dateFilter.filterNights}
                    trackingOffer={offerById.get(offerId)}
                  />
                ))}
              </CardCarousel>
            </OfferListEventsProvider>
          )}
        </CarouselWrapper>
      </CardWrapper>
    </Container>
  )
}

export default memo(ChatHotelCarousel)
